/**
 * Uploads that outlive the page they started on.
 *
 * A file goes to storage from here, not from the New e-file screen, so moving to another
 * screen does not stop it. Big files go up in 4 MB blocks, each one retried on its own, and
 * are committed at the end; small files go up in one request. Progress and the upload's
 * validation state are published to the header tray and to the wizard.
 *
 * What a browser cannot do: keep a file across a full reload. The task list is saved, so
 * after a reload a task that was mid-transfer shows as interrupted with the blocks already
 * sent remembered; choosing the same file again resumes from there. Tasks whose file had
 * fully arrived carry on: the server keeps validating regardless, and the tray keeps polling.
 */
import type { Upload, UploadFormat } from '@tabservice/tabfile-client';
import { tabfile } from '@/lib/api';

export type UploadPhase = 'uploading' | 'interrupted' | 'uploaded' | 'processing' | 'checked' | 'blocked' | 'failed';

export interface UploadTask {
  /** The upload id the API gave us. */
  id: string;
  fileName: string;
  size: number;
  format: UploadFormat;
  formType: string | null;
  taxYear: number;
  phase: UploadPhase;
  loaded: number;
  percent: number;
  /** Blocks confirmed by storage. Resume starts after the last one. */
  blocksDone: number;
  /** The upload's record count once checked, or the reason it is blocked or failed. */
  note: string | null;
  error: string | null;
  startedAt: string;
  updatedAt: string;
}

const STORAGE_KEY = 'tabfile.uploads';
const BLOCK_BYTES = 4 * 1024 * 1024;
const SINGLE_PUT_LIMIT = 8 * 1024 * 1024;
const BLOCK_ATTEMPTS = 4;
const POLL_MS = 3000;
const KEEP_DONE_HOURS = 24;

type Listener = () => void;

class UploadManager {
  private tasks = new Map<string, UploadTask>();
  private files = new Map<string, File>();
  private listeners = new Set<Listener>();
  private snapshot: UploadTask[] = [];
  private polling = new Set<string>();
  private unloadGuard = false;

  constructor() {
    this.restore();
  }

  // ---------------------------------------------------------------- store api

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): UploadTask[] => this.snapshot;

  get(uploadId: string | null | undefined): UploadTask | undefined {
    return uploadId ? this.tasks.get(uploadId) : undefined;
  }

  /** The file for a task, when this page session still holds it. */
  fileFor(uploadId: string): File | undefined {
    return this.files.get(uploadId);
  }

  dismiss(uploadId: string): void {
    this.tasks.delete(uploadId);
    this.files.delete(uploadId);
    this.publish();
  }

  // ---------------------------------------------------------------- starting and resuming

  start(input: {
    uploadId: string;
    uploadUrl: string;
    file: File;
    format: UploadFormat;
    formType: string | null;
    taxYear: number;
  }): UploadTask {
    const now = new Date().toISOString();
    const task: UploadTask = {
      id: input.uploadId,
      fileName: input.file.name,
      size: input.file.size,
      format: input.format,
      formType: input.formType,
      taxYear: input.taxYear,
      phase: 'uploading',
      loaded: 0,
      percent: 0,
      blocksDone: 0,
      note: null,
      error: null,
      startedAt: now,
      updatedAt: now,
    };
    this.tasks.set(task.id, task);
    this.files.set(task.id, input.file);
    this.publish();
    void this.transfer(task.id, input.uploadUrl);
    return task;
  }

  /** After a reload: the same file chosen again continues where the blocks stopped. */
  async resume(uploadId: string, file: File): Promise<void> {
    const task = this.tasks.get(uploadId);
    if (!task) throw new Error('That upload is no longer listed.');
    if (file.name !== task.fileName || file.size !== task.size) {
      throw new Error(`Choose the same file: ${task.fileName} (${task.size} bytes).`);
    }
    const link = await tabfile.POST('/v1/uploads/{uploadId}/link', { params: { path: { uploadId } } });
    this.files.set(uploadId, file);
    this.update(uploadId, { phase: 'uploading', error: null });
    await this.transfer(uploadId, link.uploadUrl);
  }

  /** Retries a failed transfer with the file this session still holds. */
  async retry(uploadId: string): Promise<void> {
    const file = this.files.get(uploadId);
    if (!file) throw new Error('Choose the file again to continue this upload.');
    await this.resume(uploadId, file);
  }

  // ---------------------------------------------------------------- the transfer itself

  private async transfer(uploadId: string, uploadUrl: string): Promise<void> {
    const file = this.files.get(uploadId);
    const task = this.tasks.get(uploadId);
    if (!file || !task) return;
    this.guardUnload();
    try {
      if (file.size <= SINGLE_PUT_LIMIT) {
        await this.putWhole(uploadId, uploadUrl, file);
      } else {
        await this.putBlocks(uploadId, uploadUrl, file);
      }
      this.update(uploadId, { phase: 'uploaded', loaded: file.size, percent: 100 });
      this.files.delete(uploadId);
      this.poll(uploadId);
    } catch (error) {
      this.update(uploadId, {
        phase: 'interrupted',
        error: error instanceof Error ? error.message : 'The upload stopped.',
      });
    } finally {
      this.guardUnload();
    }
  }

  private putWhole(uploadId: string, url: string, file: File): Promise<void> {
    return this.xhrPut(url, file, { 'x-ms-blob-type': 'BlockBlob' }, (loaded) => {
      this.update(uploadId, { loaded, percent: Math.round((loaded / file.size) * 100) });
    });
  }

  private async putBlocks(uploadId: string, url: string, file: File): Promise<void> {
    const total = Math.ceil(file.size / BLOCK_BYTES);
    const ids: string[] = [];
    for (let index = 0; index < total; index += 1) ids.push(blockId(index));

    const task = this.tasks.get(uploadId);
    let index = task?.blocksDone ?? 0;
    for (; index < total; index += 1) {
      const chunk = file.slice(index * BLOCK_BYTES, Math.min(file.size, (index + 1) * BLOCK_BYTES));
      const blockUrl = `${url}&comp=block&blockid=${encodeURIComponent(ids[index])}`;
      let attempt = 0;
      for (;;) {
        try {
          await this.xhrPut(blockUrl, chunk, {}, (loaded) => {
            const done = index * BLOCK_BYTES + loaded;
            this.update(uploadId, { loaded: done, percent: Math.round((done / file.size) * 100) });
          });
          break;
        } catch (error) {
          attempt += 1;
          if (attempt >= BLOCK_ATTEMPTS) throw error;
          await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
        }
      }
      this.update(uploadId, { blocksDone: index + 1 });
    }

    const body = `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids.map((id) => `<Latest>${id}</Latest>`).join('')}</BlockList>`;
    await this.xhrPut(`${url}&comp=blocklist`, body, {}, () => undefined);
  }

  private xhrPut(
    url: string,
    body: Blob | string,
    headers: Record<string, string>,
    onProgress: (loaded: number) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('PUT', url, true);
      // text/plain is CORS-safelisted, so the storage account needs no extra allowance.
      request.setRequestHeader('Content-Type', 'text/plain');
      for (const [name, value] of Object.entries(headers)) request.setRequestHeader(name, value);
      request.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded);
      };
      request.onload = () => {
        if (request.status >= 200 && request.status < 300) resolve();
        else if (request.status === 403) reject(new Error('The upload link expired. Choose the file again to continue.'));
        else reject(new Error('The file could not be stored. Try again, and contact Tab Service if it continues.'));
      };
      request.onerror = () => reject(new Error('The connection dropped during the upload.'));
      request.send(body);
    });
  }

  /**
   * After a reload, a task that was mid-transfer is either interrupted (the file never fully
   * arrived) or already being checked (it had). Only the server can say which.
   */
  private async reconcile(uploadId: string): Promise<void> {
    try {
      const upload = (await tabfile.GET('/v1/uploads/{uploadId}', { params: { path: { uploadId } } })) as Upload;
      if (upload.status === 'awaiting_file') {
        this.update(uploadId, { phase: 'interrupted', error: 'The page was reloaded before the file finished uploading.' });
        return;
      }
      this.update(uploadId, { phase: 'uploaded', percent: 100 });
      this.poll(uploadId);
    } catch {
      this.update(uploadId, { phase: 'interrupted', error: 'The page was reloaded before the file finished uploading.' });
    }
  }

  /** Forgets every upload, for a sign-out: the next person must not see this one's files. */
  forgetAll(): void {
    this.tasks.clear();
    this.files.clear();
    this.polling.clear();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing stored.
    }
    this.publish();
  }

  // ---------------------------------------------------------------- following validation

  private poll(uploadId: string): void {
    if (this.polling.has(uploadId)) return;
    this.polling.add(uploadId);
    const tick = async () => {
      const task = this.tasks.get(uploadId);
      if (!task) {
        this.polling.delete(uploadId);
        return;
      }
      try {
        const upload = (await tabfile.GET('/v1/uploads/{uploadId}', { params: { path: { uploadId } } })) as Upload;
        const status = upload.status;
        if (status === 'checked' || status === 'filed') {
          this.update(uploadId, {
            phase: 'checked',
            formType: upload.formType ?? task.formType,
            note: `${(upload.recordCount ?? 0).toLocaleString()} records ready`,
          });
          this.polling.delete(uploadId);
          return;
        }
        if (status === 'blocked' || status === 'failed') {
          const issue = upload.check?.issues?.[0]?.message ?? upload.parseErrors?.[0]?.message ?? null;
          this.update(uploadId, { phase: status === 'blocked' ? 'blocked' : 'failed', note: issue });
          this.polling.delete(uploadId);
          return;
        }
        if (status === 'awaiting_file') {
          // The server is still waiting for the file: the transfer never finished.
          this.update(uploadId, { phase: 'interrupted', error: 'The file did not finish uploading.' });
          this.polling.delete(uploadId);
          return;
        }
        this.update(uploadId, { phase: 'processing' });
      } catch {
        // A blip while polling is not a failed upload. Ask again next tick.
      }
      setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
  }

  // ---------------------------------------------------------------- housekeeping

  private update(uploadId: string, patch: Partial<UploadTask>): void {
    const task = this.tasks.get(uploadId);
    if (!task) return;
    this.tasks.set(uploadId, { ...task, ...patch, updatedAt: new Date().toISOString() });
    this.publish();
  }

  private publish(): void {
    this.snapshot = [...this.tasks.values()].sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    this.persist();
    for (const listener of this.listeners) listener();
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.snapshot));
    } catch {
      // Storage unavailable: the tray simply does not survive a reload.
    }
  }

  private restore(): void {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const cutoff = Date.now() - KEEP_DONE_HOURS * 3600_000;
      for (const task of JSON.parse(raw) as UploadTask[]) {
        if (new Date(task.updatedAt).getTime() < cutoff) continue;
        this.tasks.set(task.id, task);
      }
      this.snapshot = [...this.tasks.values()];
      for (const task of this.tasks.values()) {
        if (task.phase === 'uploaded' || task.phase === 'processing') this.poll(task.id);
        // A transfer cannot survive a reload, but it may have finished just before it. The
        // server knows whether the file arrived; ask before declaring it interrupted.
        if (task.phase === 'uploading') void this.reconcile(task.id);
      }
    } catch {
      // Unreadable state is dropped rather than trusted.
    }
  }

  private guardUnload(): void {
    if (typeof window === 'undefined') return;
    const uploading = [...this.tasks.values()].some((task) => task.phase === 'uploading');
    if (uploading && !this.unloadGuard) {
      window.addEventListener('beforeunload', beforeUnload);
      this.unloadGuard = true;
    } else if (!uploading && this.unloadGuard) {
      window.removeEventListener('beforeunload', beforeUnload);
      this.unloadGuard = false;
    }
  }
}

function beforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault();
  // Browsers show their own wording; the returnValue is what older ones need.
  event.returnValue = '';
}

function blockId(index: number): string {
  return btoa(String(index).padStart(8, '0'));
}

export const uploadManager = new UploadManager();

/** True while a task is still moving: uploading or being validated. */
export function isActive(task: UploadTask): boolean {
  return task.phase === 'uploading' || task.phase === 'uploaded' || task.phase === 'processing';
}
